/* eslint-disable @nx/enforce-module-boundaries */
import {
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, filter, of, Subscription, switchMap } from 'rxjs';
import { KanziPrimeUiModule, MenuItem } from '@kanzi-apes/kanzi-prime-ui';
import {
  KanziUiTopBarComponent,
  MainMenuService,
  KanziUiMainSidebarComponent,
  LayoutService,
  KanziUiBreadcrumbComponent,
} from '@kanzi-apes/kanzi-ui';
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { ItemsMenu } from '../configs/main-menu';
import kanziVersion from 'kanziVersion.json';
import * as Auth from '@kanzi-apes/kanzi-authenticator';
import { LocationsQueries } from '@kanzi-apes/kanzi-locations';
import * as Locations from '@kanzi-apes/kanzi-locations';
import { WarehouseModel } from '@kanzi-apes/kanzi-models';
import { AlertVoiceService, KanziAlertsModule } from '@kanzi-apes/kanzi-alerts';
import { KanziCustomerModule } from '@kanzi-apes/kanzi-customer';
import { _, TranslateModule, TranslateService } from '@ngx-translate/core';
import * as layoutStore from '../+state/layout/layout.reducer';
import * as layoutSelectors from '../+state/layout/layout.selectors';
import * as layoutActions from '../+state/layout/layout.actions';
import * as MonitoringStore from '../+state/monitoring/monitoring.reducer';
import * as MonitoringSelectors from '../+state/monitoring/monitoring.selectors';
import * as MonitoringActions from '../+state/monitoring/monitoring.actions';
import { UsersWMSService } from '@kanzi-apes/kanzi-wms-users';

import { environment } from '../../environments/environment';
import { KanziAnalyticsReportsService } from '@kanzi-apes/kanzi-analytics-reports';
import { menuWMSValidator } from '@kanzi-apes/kanzi-ui-configs';

@Component({
    selector: 'kanzi-apes-layout',
    imports: [
        CommonModule,
        KanziPrimeUiModule,
        KanziUiModule,
        KanziAlertsModule,
        KanziCustomerModule,
        KanziUiTopBarComponent,
        KanziUiMainSidebarComponent,
        TranslateModule,
        KanziUiBreadcrumbComponent,
    ],
    providers: [],
    templateUrl: './layout.component.html',
    styleUrl: './layout.component.scss'
})
export class LayoutComponent implements OnInit, OnDestroy {
  layoutService: LayoutService;
  analyticsReportsService = inject(KanziAnalyticsReportsService);
  translateService: TranslateService = inject(TranslateService);
  mainMenuService: MainMenuService = inject(MainMenuService);
  router: Router = inject(Router);
  storeAuth: Store<Auth.AuthState> = inject(Store);
  storeLocations: Store<Locations.LocationsState> = inject(Store);
  storeLayout: Store<layoutStore.LayoutState> = inject(Store);
  storeMonitoring: Store<MonitoringStore.MonitoringState> = inject(Store);
  authService = inject(Auth.AuthService);
  locationsQueries = inject(LocationsQueries);
  alertVoiceService = inject(AlertVoiceService);
  usersWMSService = inject(UsersWMSService);
  warehouseListQuery = this.locationsQueries.warehouseRootListData;
  progressBar = signal(true);
  itemsMenu = signal<MenuItem[]>([]);
  kanziAppVersion = signal('');
  dataloaderHide = signal(false);
  userProfile = this.storeAuth.selectSignal(Auth.getAuthUser);
  userConfigs = this.storeAuth.selectSignal(Auth.getAuthUserWMSConfig);
  userWarehouseRootSelected = this.storeLocations.selectSignal(
    Locations.getWarehouseRootSelected
  );
  warehouseRootList = this.storeLocations.selectSignal(
    Locations.getWarehouseRootList
  );
  languageSelected = this.storeLayout.selectSignal(
    layoutSelectors.getLayoutLanguage
  );
  monitoringStatus = this.storeMonitoring.selectSignal(
    MonitoringSelectors.getMonitoringStatus
  );
  monitoringStatusMsg = this.storeMonitoring.selectSignal(
    MonitoringSelectors.getMonitoringStatusMsg
  );

  overlayMenuOpenSubscription: Subscription;
  topbarMenuOpenSubscription: Subscription;
  menuProfileOpenSubscription: Subscription;
  menuOutsideClickListener: any;
  menuScrollListener: any;
  topbarMenuOutsideClickListener: any;
  menuProfileOutsideClickListener: any;

  @ViewChild(KanziUiTopBarComponent) appTopbar!: KanziUiTopBarComponent;
  @ViewChild(KanziUiMainSidebarComponent)
  appSidebar!: KanziUiMainSidebarComponent;

  userConfigsEffects = effect(
    () => {
      const userConfigs = this.userConfigs();
      if (userConfigs) {
        const permissionList = userConfigs.roles.flatMap((role) =>
          role.web_modules.flatMap((module) => module.module)
        );
        console.log('Permissions WMS:', permissionList);
        this.itemsMenu.set(menuWMSValidator(ItemsMenu, permissionList));
      }
    },
    { allowSignalWrites: true }
  );

  layoutEffects = effect(
    () => {
      const userConfigs = this.userConfigs();
      this.dataloaderHide.set(environment.dataloaderHide);
      if (userConfigs) {
        const warehouseRoots = this.warehouseListQuery();
        if (warehouseRoots) {
          const allowedWarehouseIds =
            userConfigs.warehouses?.map((w) => w.id) || [];
          // Filter warehouses based on status and user permissions
          const filteredWarehouses = {
            ...warehouseRoots,
            results: warehouseRoots.results.filter(
              (warehouse) =>
                // warehouse.status === 'AVAILABLE' &&
                (allowedWarehouseIds.includes(warehouse.id) ||
                  userConfigs.roles.some(
                    (r) =>
                      r.name === 'super_admin' || r.name === 'admin'
                  ))
            ),
          };
          this.storeLocations.dispatch(
            Locations.loadWarehouseRootListSuccess({
              listItems: filteredWarehouses,
            })
          );

          // Find default warehouse if specified in user config
          const defaultWarehouseId = userConfigs.default_location_id;
          const defaultWarehouse = defaultWarehouseId
            ? filteredWarehouses.results.find(
                (warehouse) => warehouse.id === defaultWarehouseId
              )
            : null;

          // Set default warehouse in store if found
          if (defaultWarehouse) {
            untracked(() => {
              this.storeLocations.dispatch(
                Locations.warehouseRootIdSelected({
                  warehouseRootId: defaultWarehouseId,
                })
              );

              this.storeLocations.dispatch(
                Locations.selectWarehouseRoot({
                  warehouseRoot: defaultWarehouse,
                })
              );
            });
          }
        }
        if (environment.tenantId) {
          this.analyticsReportsService
            .getReportList(environment.tenantId, true)
            .subscribe((dashboardList) => {
              if (dashboardList && dashboardList.count > 0) {
                // Find the index of the dashboards menu
                const dashboardIndex = ItemsMenu.findIndex(
                  (items) => items.label === 'menu_main.dashboards'
                );
                // Add the dashboards to the menu
                ItemsMenu[dashboardIndex].items = dashboardList.dashboards.map(
                  (item) => {
                    let menuItem: MenuItem = {
                      label: item.dashboardMenuLabel || item.dashboardName,
                      icon: item.dashboardMenuIcon || 'pi pi-fw pi-chart-line',
                      routerLink: item.routerLink || 'analytics/embed-report',
                    };
                    if (!item.withoutQueryParams) {
                      menuItem = {
                        ...menuItem,
                        queryParams: {
                          reportName: item.dashboardName,
                        },
                      };
                    }
                    return menuItem;
                  }
                );
                this.itemsMenu.set(ItemsMenu);
              }
            });
        }
      }
    },
    { allowSignalWrites: true }
  );

  langEffects = effect(() => {
    this.translateService.use(this.languageSelected());
  });

  monitoringEffects = effect(() => {
    if (
      this.monitoringStatus() &&
      this.monitoringStatus().actual !== this.monitoringStatus().previous
    ) {
      const msg = this.monitoringStatusMsg();
      if (msg) {
        this.alertVoiceService.message(msg);
      }
    }
  });

  constructor(public renderer: Renderer2) {
    this.layoutService = inject(LayoutService);
    this.kanziAppVersion.set(kanziVersion.version);
    this.hideMenuProfile();

    this.overlayMenuOpenSubscription =
      this.layoutService.overlayOpen$.subscribe(() => {
        this.hideTopbarMenu();

        if (!this.menuOutsideClickListener) {
          this.menuOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appSidebar.el.nativeElement.isSameNode(event.target) ||
                this.appSidebar.el.nativeElement.contains(event.target) ||
                this.appTopbar.menuButton.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appTopbar.menuButton.nativeElement.contains(event.target)
              );
              if (isOutsideClicked) {
                this.hideMenu();
              }
            }
          );
        }

        if (
          (this.layoutService.isHorizontal() ||
            this.layoutService.isSlim() ||
            this.layoutService.isSlimPlus()) &&
          !this.menuScrollListener
        ) {
          this.menuScrollListener = this.renderer.listen(
            this.appSidebar.menuContainer.nativeElement,
            'scroll',
            () => {
              if (this.layoutService.isDesktop()) {
                this.hideMenu();
              }
            }
          );
        }

        if (this.layoutService.state.staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.topbarMenuOpenSubscription =
      this.layoutService.topbarMenuOpen$.subscribe(() => {
        if (!this.topbarMenuOutsideClickListener) {
          this.topbarMenuOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appTopbar.el.nativeElement.isSameNode(event.target) ||
                this.appTopbar.el.nativeElement.contains(event.target) ||
                this.appTopbar.mobileMenuButton.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appTopbar.mobileMenuButton.nativeElement.contains(
                  event.target
                )
              );
              if (isOutsideClicked) {
                this.hideTopbarMenu();
              }
            }
          );
        }

        if (this.layoutService.state.staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.menuProfileOpenSubscription =
      this.layoutService.menuProfileOpen$.subscribe(() => {
        this.hideMenu();

        if (!this.menuProfileOutsideClickListener) {
          this.menuProfileOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appSidebar.menuProfile.el.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appSidebar.menuProfile.el.nativeElement.contains(
                  event.target
                )
              );
              if (isOutsideClicked) {
                this.hideMenuProfile();
              }
            }
          );
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.hideMenu();
        this.hideTopbarMenu();
        this.hideMenuProfile();
      });
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi'
        ),
        ' '
      );
    }
  }

  hideMenu() {
    this.layoutService.state.overlayMenuActive = false;
    this.layoutService.state.staticMenuMobileActive = false;
    this.layoutService.state.menuHoverActive = false;
    this.mainMenuService.reset();

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }
    this.unblockBodyScroll();
  }

  hideTopbarMenu() {
    this.layoutService.state.topbarMenuActive = false;

    if (this.topbarMenuOutsideClickListener) {
      this.topbarMenuOutsideClickListener();
      this.topbarMenuOutsideClickListener = null;
    }
  }

  hideMenuProfile() {
    this.layoutService.state.menuProfileActive = false;

    if (this.menuProfileOutsideClickListener) {
      this.menuProfileOutsideClickListener();
      this.menuProfileOutsideClickListener = null;
    }
  }

  get containerClass() {
    const styleClass: { [key: string]: boolean } = {
      'layout-overlay': this.layoutService.config().menuMode === 'overlay',
      'layout-static': this.layoutService.config().menuMode === 'static',
      'layout-slim': this.layoutService.config().menuMode === 'slim',
      'layout-slim-plus': this.layoutService.config().menuMode === 'slim-plus',
      'layout-horizontal':
        this.layoutService.config().menuMode === 'horizontal',
      'layout-reveal': this.layoutService.config().menuMode === 'reveal',
      'layout-drawer': this.layoutService.config().menuMode === 'drawer',
      'p-input-filled': this.layoutService.config().inputStyle === 'filled',
      'p-ripple-disabled': !this.layoutService.config().ripple,
      'layout-static-inactive':
        this.layoutService.state.staticMenuDesktopInactive &&
        this.layoutService.config().menuMode === 'static',
      'layout-overlay-active': this.layoutService.state.overlayMenuActive,
      'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
      'layout-topbar-menu-active': this.layoutService.state.topbarMenuActive,
      'layout-menu-profile-active': this.layoutService.state.menuProfileActive,
      'layout-sidebar-active': this.layoutService.state.sidebarActive,
      'layout-sidebar-anchored': this.layoutService.state.anchored,
    };
    styleClass['layout-topbar-' + this.layoutService.config().topbarTheme] =
      true;
    styleClass['layout-menu-' + this.layoutService.config().menuTheme] = true;
    styleClass[
      'layout-menu-profile-' + this.layoutService.config().menuProfilePosition
    ] = true;
    return styleClass;
  }

  logoutSession() {
    this.storeAuth.dispatch(Auth.logout());
  }

  onSelectWarehouse(warehouseRootSelected: WarehouseModel) {
    const userConfigs = this.userConfigs();
    if (userConfigs) {
      this.usersWMSService
        .updateUserConfigs({
          ...userConfigs,
          default_location_id: warehouseRootSelected.id,
        })
        .subscribe(() => {
          this.storeLocations.dispatch(
            Locations.selectWarehouseRoot({
              warehouseRoot: warehouseRootSelected,
            })
          );
        });
    }
  }

  /**
   * Change languaje
   * @param lang {string} - Language
   */
  changeLanguage(lang: string) {
    this.storeLayout.dispatch(layoutActions.changeLanguage({ language: lang }));
  }

  /**
   *
   * @param event {any}
   *
   * Function to know if the browser is online.
   */
  updateNetworkIndicator(event: unknown) {
    const networkEvent = event as Event;
    if (networkEvent.type === 'online') {
      // this.functions?.changeFavicon('active');
      this.translateService
        .get(_(['app.online', 'app.online_msg']))
        .subscribe((words) => {
          this.storeMonitoring.dispatch(
            MonitoringActions.statusOnline({
              payload: {
                type: 'success',
                title: words['app.online'],
                msg: words['app.online_msg'],
              },
            })
          );
        });
    } else if (networkEvent.type === 'offline') {
      // this.functions?.changeFavicon('error');
      this.translateService
        .get(_(['app.offline', 'app.offline_msg']))
        .subscribe((words) => {
          this.storeMonitoring.dispatch(
            MonitoringActions.statusOffline({
              payload: {
                type: 'error',
                title: words['app.offline'],
                msg: words['app.offline_msg'],
              },
            })
          );
        });
    }
  }

  ngOnInit() {
    this.translateService.use(this.languageSelected());
    this.progressBar.set(false);
    this.authService
      .checkAndRefreshToken()
      .pipe(
        switchMap((session) => {
          if (session && session.isValid()) {
            return this.authService.getCurrentUserData();
          }

          this.storeAuth.dispatch(Auth.loginRedirect());
          return of(null);
        }),
        catchError((error) => {
          this.storeAuth.dispatch(Auth.loginRedirect());
          return of(null);
        })
      )
      .subscribe((userData) => {
        if (userData) {
          this.storeAuth.dispatch(Auth.userProfileSuccess({ user: userData }));
          this.storeAuth.dispatch(
            Auth.loadUserConfigsWMS({
              userConfigFilters: {
                merge_rol_actions: true,
                username__eq: userData.Username,
              },
            })
          );
          this.locationsQueries.loadAllWarehouseRootPages();
        }
      });
    window.addEventListener('online', this.updateNetworkIndicator.bind(this));
    window.addEventListener('offline', this.updateNetworkIndicator.bind(this));
  }

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }

    this.layoutEffects.destroy();
  }
}
